import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';

import { ChannelStatus } from '../../../../enums/ChannelStatus';
import Tooltip from '../../../Tooltip';
import { ChatStatusMessage, Disclaimer, SendIconButton } from '../../styled';
import ChatBlockedWarning from '../ChatBlockedWarning/ChatBlockedWarning';
import ChatError from '../ChatError/ChatError';
import AgentButton from './AgentButton';
import { MessageBoxDivider, MessageBoxRow, MessageInput, SendIcon, TextAreaContainer } from './MessageSectionStyles';
import MuteButton from './MuteButton';

const MessageSection = inject('chatStore')(
  observer((props) => {
    const [inputValue, setInputValue] = useState('');

    if (!props.chatStore.options) {
      props.chatStore.options = {};
    }
    if (!props.chatStore.status) {
      props.chatStore.status = ChannelStatus.Unknown;
    }

    const { status, loading, error, errorRetry, isCustomerBlocked, botEnabled, isEscalating, hasEscalated } =
      props.chatStore;

    const onEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.keyCode === 13) {
        e.currentTarget.style.height = '34px';
        // prevent next line
        e.preventDefault();
        if (e.currentTarget.value.trim()) {
          sendMessage(e.currentTarget.value);
        }
      }
    };

    const sendMessage = (value: string) => {
      if (!loading) {
        if (props.chatStore.messages.length === 1) {
          props.chatStore.sendMessageWithGreeting(value);
        } else {
          // send message
          props.chatStore.sendMessage(value);
        }
        // clear input
        setInputValue('');

        // reset height
        document.getElementById('hxc4web-webchat-message-box')!.style.cssText = 'height:34px';
      }
    };

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
      setTextAreaHeight(e.target);
      props.chatStore.sendTyping();
    };

    const setTextAreaHeight = (target: HTMLTextAreaElement) => {
      target.style.cssText = 'height:auto; padding:0';
      target.style.cssText = 'height:' + target.scrollHeight + 'px';
    };

    const muteTooltip = props.chatStore.audioNotification ? 'Mute' : 'Unmute';

    return (
      <React.Fragment>
        <Disclaimer>
          By continuing to chat, you consent to this chat being recorded and stored in our chat service in accordance
          with our Privacy Policy.
        </Disclaimer>
        {isCustomerBlocked && <ChatBlockedWarning />}
        {error && !isCustomerBlocked && <ChatError errorMessage={error.message} errorRetry={errorRetry} />}
        {status === ChannelStatus.Waiting && <ChatStatusMessage>Please wait while we connect you.</ChatStatusMessage>}
        {status === ChannelStatus.Transferred && (
          <ChatStatusMessage>Please wait while we transfer you to an agent.</ChatStatusMessage>
        )}
        {status === ChannelStatus.Ended && (
          <ChatStatusMessage>Your chat has ended. Thanks for reaching out to us.</ChatStatusMessage>
        )}
        {isEscalating && !hasEscalated && <ChatStatusMessage>Waiting for an agent...</ChatStatusMessage>}
        <MessageBoxRow>
          <MessageBoxDivider />
          <TextAreaContainer>
            <MessageInput
              rows={2}
              placeholder='Type your message here'
              value={inputValue}
              onChange={onChange}
              onKeyDown={onEnter}
              id='hxc4web-webchat-message-box'
            />
          </TextAreaContainer>
          <SendIconButton disabled={loading} onClick={() => sendMessage(inputValue)}>
            <SendIcon />
          </SendIconButton>
        </MessageBoxRow>
        <div style={{ padding: 10, paddingTop: 0 }}>
          <Tooltip content={muteTooltip} direction='top'>
            <MuteButton />
          </Tooltip>
          {botEnabled && (
            <Tooltip content='Talk to an agent' direction='top'>
              <AgentButton />
            </Tooltip>
          )}
        </div>
      </React.Fragment>
    );
  }),
);

export default MessageSection;
